import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export class PhoneValidator {
  /**
   * USA phone number formats supported (NO country code allowed):
   * - (123) 456-7890
   * - 123-456-7890
   * - 123.456.7890
   * - 123 456 7890
   * - 1234567890
   */
  static readonly USA_PHONE_PATTERN = /^\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;

  /**
   * Strict USA phone number pattern (10 digits only, no country code)
   * Formats: 1234567890, 123-456-7890, (123) 456-7890, 123.456.7890, 123 456 7890
   */
  static readonly USA_PHONE_STRICT_PATTERN = /^(\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4})$/;

  /**
   * Pattern to detect country codes (starts with + followed by digits)
   */
  static readonly COUNTRY_CODE_PATTERN = /^\+/;

  /**
   * Validator function for USA phone numbers (NO country code allowed)
   * @param control - The form control to validate
   * @returns ValidationErrors | null
   */
  static usaPhone(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const phoneNumber = control.value.toString().trim();
    
    // Check for country code (starts with +)
    if (PhoneValidator.COUNTRY_CODE_PATTERN.test(phoneNumber)) {
      return { 
        usaPhone: { 
          value: phoneNumber,
          message: 'Country code is not allowed. only US phone number is allowed '
        } 
      };
    }

    // Extract digits only to validate
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    
    // Check if it has 11 digits (might have country code 1)
    if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      return { 
        usaPhone: { 
          value: phoneNumber,
          message: 'Country code is not allowed. only US phone number is allowed '
        } 
      };
    }

    // Check if it matches USA phone pattern (10 digits only)
    if (!PhoneValidator.USA_PHONE_PATTERN.test(phoneNumber)) {
      return { 
        usaPhone: { 
          value: phoneNumber,
          message: 'Invalid phone number format'
        } 
      };
    }

    // Validate that we have exactly 10 digits
    if (digitsOnly.length !== 10) {
      return { 
        usaPhone: { 
          value: phoneNumber,
          message: 'Phone number must contain exactly 10 digits'
        } 
      };
    }

    const phoneDigits = digitsOnly;

    // Validate area code (first 3 digits)
    const areaCode = phoneDigits.substring(0, 3);
    if (areaCode.charAt(0) === '0' || areaCode.charAt(0) === '1') {
      return { 
        usaPhone: { 
          value: phoneNumber,
          message: 'Area code cannot start with 0 or 1'
        } 
      };
    }

    // Validate exchange code (middle 3 digits)
    const exchangeCode = phoneDigits.substring(3, 6);
    if (exchangeCode.charAt(0) === '0' || exchangeCode.charAt(0) === '1') {
      return { 
        usaPhone: { 
          value: phoneNumber,
          message: 'Exchange code cannot start with 0 or 1'
        } 
      };
    }

    return null; // Valid phone number
  }

  /**
   * Validator function for strict USA phone numbers (no country code allowed)
   * @param control - The form control to validate
   * @returns ValidationErrors | null
   */
  static usaPhoneStrict(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const phoneNumber = control.value.toString().trim();
    
    if (!PhoneValidator.USA_PHONE_STRICT_PATTERN.test(phoneNumber)) {
      return { 
        usaPhoneStrict: { 
          value: phoneNumber,
          message: 'Please enter a valid  phone number without country code '
        } 
      };
    }

    return PhoneValidator.usaPhone(control);
  }

  /**
   * Format a US phone number for display.
   * Default: (XXX) XXX-XXXX (receipts / shop contact).
   * With `{ withCountryCode: true }`: +1 (XXX) XXX-XXXX.
   * Accepts optional leading +1/1. Invalid → original value.
   * Use `emptyFallback` (e.g. '-') when empty should not stay blank.
   */
  static formatUsaPhone(
    phoneNumber: string | null | undefined,
    options: { withCountryCode?: boolean; emptyFallback?: string } = {}
  ): string {
    const { withCountryCode = false, emptyFallback } = options;

    if (phoneNumber == null || String(phoneNumber).trim() === '') {
      return emptyFallback !== undefined ? emptyFallback : (phoneNumber ?? '');
    }

    const original = String(phoneNumber);
    const digits = PhoneValidator.stripFormatting(original.trim());
    const national =
      digits.length === 10
        ? digits
        : digits.length === 11 && digits.startsWith('1')
          ? digits.slice(1)
          : null;

    // Validate NANP on national digits (isValidUsaPhone rejects leading +1, so check stripped form).
    if (!national || !PhoneValidator.isValidUsaPhone(national)) {
      return original;
    }

    const local = `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
    return withCountryCode ? `+1 ${local}` : local;
  }

  /** Display helper: +1 (XXX) XXX-XXXX; empty → '-'. */
  static formatUsaPhoneDisplay(phone: string | null | undefined): string {
    return PhoneValidator.formatUsaPhone(phone, {
      withCountryCode: true,
      emptyFallback: '-',
    });
  }

  /**
   * Remove formatting from phone number (digits only)
   * @param phoneNumber - Formatted phone number
   * @returns Digits only string
   */
  static stripFormatting(phoneNumber: string): string {
    if (!phoneNumber) return phoneNumber;
    return phoneNumber.replace(/\D/g, '');
  }

  /**
   * Check if phone number is valid USA format
   * @param phoneNumber - Phone number to validate
   * @returns boolean
   */
  static isValidUsaPhone(phoneNumber: string): boolean {
    if (!phoneNumber) return false;
    
    const control = { value: phoneNumber } as AbstractControl;
    return PhoneValidator.usaPhone(control) === null;
  }
}

/**
 * Factory function to create USA phone validator
 * @returns ValidatorFn
 */
export function usaPhoneValidator(): ValidatorFn {
  return PhoneValidator.usaPhone;
}

/**
 * Factory function to create strict USA phone validator
 * @returns ValidatorFn
 */
export function usaPhoneStrictValidator(): ValidatorFn {
  return PhoneValidator.usaPhoneStrict;
}

/**
 * Custom-input rule for payment-link phone fields (matches API: 10-digit US, no country code).
 */
export const USA_PHONE_CUSTOM_INPUT_RULE = {
  type: 'custom' as const,
  value: (phone: string) => PhoneValidator.isValidUsaPhone((phone ?? '').trim()),
  message: 'Please enter a valid US phone number',
};
